import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import clsx from 'clsx'

import type { ConsentState } from '@/hooks/useCookieConsent'
import CookieSwitch from './CookieSwitch'

interface Props {
	consent: ConsentState
	onClose: () => void
	onAcceptAll: () => void
	onSaveCustom: (analytics: boolean, advertising: boolean) => void
}

const NECESSARY_DESC = 'จำเป็นสำหรับการทำงานของเว็บไซต์ ช่วยให้เข้าถึงข้อมูลและใช้เว็บไซต์ได้อย่างปลอดภัย เช่น Session ID, User ID, Cookied ID, Web Log, Security และ Privacy เป็นต้น'
const ANALYTICS_DESC = 'ช่วยให้บริษัทวิเคราะห์การทำงานเพื่อพัฒนาประสิทธิภาพให้ตอบสนองความต้องการและการใช้งานได้ดียิ่งขึ้น เช่น Google Analytics, Google Tag Manager, Hotjar เป็นต้น'
const ADVERTISING_DESC = 'ช่วยให้เราเข้าใจความต้องการของคุณมากยิ่งขึ้นและนำเสนอสิทธิประโยชน์ให้เหมาะสมกับความสนใจมากที่สุด เช่น Google Ads'

/**
 * @component ConsentModal
 * @description Cookie consent settings modal with per-category toggle controls.
 */
const ConsentModal: FC<Props> = ({ consent, onClose, onAcceptAll, onSaveCustom }) => {
	const [analytics, setAnalytics] = useState(consent.analytics)
	const [advertising, setAdvertising] = useState(consent.advertising)
	const overlayRef = useRef<HTMLDivElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		document.body.style.overflow = 'hidden'
		return () => { document.body.style.overflow = '' }
	}, [])

	useGSAP(() => {
		gsap.timeline()
			.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'none' })
			.fromTo(panelRef.current, { opacity: 0, scale: 0.95, y: 12 }, { opacity: 1, scale: 1, y: 0, duration: 0.25, ease: 'power2.out' }, '<')
	}, [])

	const animateOut = useCallback((cb: () => void) => {
		gsap.timeline({ onComplete: cb })
			.to(panelRef.current, { opacity: 0, scale: 0.95, y: 12, duration: 0.2, ease: 'power2.in' })
			.to(overlayRef.current, { opacity: 0, duration: 0.15, ease: 'none' }, '<')
	}, [])

	return (
		<div
			ref={ overlayRef }
			className={clsx(
				'fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-0',
				'sm:px-4'
			)}
			onClick={ () => animateOut(onClose) }
		>
			<div
				ref={ panelRef }
				className={clsx(
					'bg-white w-full flex flex-col shadow-xl max-h-dvh h-full',
					'sm:h-fit sm:max-h-[calc(100dvh-4rem)] sm:max-w-202.5 sm:p-10 sm:gap-8 sm:rounded-2xl'
				)}
				onClick={ (e) => e.stopPropagation() }
			>
				<button
					type="button"
					onClick={ () => animateOut(onClose) }
					className={clsx(
						'flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:opacity-50 transition cursor-pointer absolute top-4 right-4',
						'sm:top-10 sm:right-10'
					)}
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path d="M5 5L19 19M5 19L19 5" stroke="#191B23" strokeWidth="1.5"/>
					</svg>
				</button>

				{/* ── Header ── */}
				<div className={clsx(
					'flex justify-between items-center px-6 pt-10',
					'sm:px-0 sm:pt-0'
				)}>
					<h2 className="primary/subtitle font-medium text-gray-800">ตั้งค่าความยินยอม</h2>
				</div>

				{/* ── Category rows ── */}
				<div className={clsx(
					'flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 px-6 py-4 pb-10 max-sm:no-scrollbar',
					'sm:px-0 sm:py-0'
				)}>
					{/* Necessary */}
					<div className="p-4 sm:p-6 flex flex-col justify-between items-start gap-2.5 bg-gray-25 border border-gray-100 rounded-2xl">
						<div className="flex justify-between items-start w-full">
							<p className="primary/paragraph font-medium text-gray-800">คุกกี้พื้นฐานที่จำเป็น</p>
							<CookieSwitch checked disabled />
						</div>
						<p className="paragraph/paragraph font-normal text-gray-500">{ NECESSARY_DESC }</p>
					</div>

					{/* Analytics */}
					<div className="p-4 sm:p-6 flex flex-col justify-between items-start gap-2.5 bg-gray-25 border border-gray-100 rounded-2xl">
						<div className="flex justify-between items-start w-full">
							<p className="primary/paragraph font-medium text-gray-800">คุกกี้ประเภทการวิเคราะห์</p>
							<CookieSwitch checked={ analytics } onChange={ () => setAnalytics(prev => !prev) } />
						</div>
						<p className="paragraph/paragraph font-normal text-gray-500">{ ANALYTICS_DESC }</p>
					</div>

					{/* Advertising */}
					<div className="p-4 sm:p-6 flex flex-col justify-between items-start gap-2.5 bg-gray-25 border border-gray-100 rounded-2xl">
						<div className="flex justify-between items-start w-full">
							<p className="primary/paragraph font-medium text-gray-800">คุกกี้ประเภทการโฆษณา</p>
							<CookieSwitch checked={ advertising } onChange={ () => setAdvertising(prev => !prev) } />
						</div>
						<p className="paragraph/paragraph font-normal text-gray-500">{ ADVERTISING_DESC }</p>
					</div>
				</div>

				{/* ── Footer buttons ── */}
				<div className="relative px-6 pb-6 sm:px-0 sm:pb-0">
					{/* Gradient fade — mobile only */}
					<div className="absolute -top-8 inset-x-0 h-8 bg-gradient-to-b from-transparent to-white pointer-events-none sm:hidden" />
					<div className="flex justify-end items-center gap-3">
						<button
							type="button"
							onClick={ () => animateOut(() => onSaveCustom(analytics, advertising)) }
							className={clsx(
								'flex flex-1 items-center justify-center h-12 w-full rounded-full border-2 border-gray-800 bg-white primary/small font-medium text-gray-800 cursor-pointer transition-colors',
								'hover:bg-gray-800 hover:text-white',
								'sm:px-6 sm:flex-none sm:w-fit'
							)}
						>
							ยืนยันตัวเลือก
						</button>
						<button
							type="button"
							onClick={ () => animateOut(onAcceptAll) }
							className={clsx(
								'flex flex-1 items-center justify-center h-12 w-full rounded-full bg-gray-800 primary/small font-medium text-white cursor-pointer transition-colors',
								'hover:bg-gray-700',
								'sm:flex-0 sm:w-fit sm:px-6'
							)}
						>
							<span className="primary/small font-medium whitespace-nowrap">ยอมรับทั้งหมด</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ConsentModal
